import { Injectable } from '@angular/core';
import { I18nService } from './i18nService';
import { GlobalNotificationHandler } from '../utils/notifications/global-notification-handler';

@Injectable({
    providedIn: 'root'
})
export class CKEditorService {
    protected rowCount: number = 1;
    
    constructor(protected notificationService: GlobalNotificationHandler, protected translate: I18nService) {
    }

    public rows(rows: number = 1): CKEditorService
    {
        this.rowCount = rows;

        return this;
    }

    public initWordCount(editor: any, containerId: string, maxLength?: number) {
        const pixelPerLine = 40;
        const defaultAnzahlDerZeilen = this.rowCount;
        const minHeightInPixels = defaultAnzahlDerZeilen * pixelPerLine;

        editor.editing.view.change((writer: any) => { 
            writer.setStyle('min-height', minHeightInPixels + 14.4 + 'px', editor.editing.view.document.getRoot()); 
        });
        
        const wordCountPlugin = editor.plugins.get('WordCount');
        const wordCountWrapper = document.getElementById(containerId);
        var countContainer = wordCountPlugin.wordCountContainer;
        wordCountWrapper?.appendChild(countContainer);

        if (maxLength != null && maxLength > 0) {
            this.initMaxLengthWordCount(editor, containerId, maxLength);
            this.initMaxLengthCopyPaste(editor, maxLength);
            this.initMaxLengthLink(editor, maxLength);
            this.initMaxLengthSelectionReplace(editor, maxLength);
            this.validateLength(editor, maxLength);
        }
    }

    public initMaxLengthLink(editor: any, maxLength: number) {
        editor.commands.get('link').on('execute', (evt: any, data: any) => {
            let currentLengthOfField = editor.plugins.get('WordCount').characters;
        
            if(currentLengthOfField > maxLength) {
                let regex = new RegExp("<a href=\"" + data[0] + "\">" + data[0] + "</a>");
                let replacedData = editor.getData().replace(regex, "<a href=\"" + data[0] + "\">" + data[0].substring(0, maxLength - (currentLengthOfField-data[0].length)) + "</a>");
                editor.setData(replacedData);
            }
        })
    }

    public initMaxLengthWordCount(editor: any, containerId: string, maxLength: number): void {
        editor.plugins.get('WordCount').on('change:characters', ( stats: any ) => {
            let charCounter = <HTMLElement>document.getElementById(containerId)?.firstChild?.childNodes[1];
            if (charCounter) {
                charCounter.textContent = this.translate.instant("wordcounter.characters") + ": " + stats.source.characters + " / " + maxLength;
                charCounter.title = charCounter.textContent;
            }
            });
        editor.plugins.get('WordCount').fire('change:characters');

        editor.plugins.get('WordCount').on('change:words', ( stats: any ) => {
            let charCounter = <HTMLElement>document.getElementById(containerId)?.firstChild?.firstChild;
            if (charCounter) {
                charCounter.textContent = this.translate.instant("wordcounter.words") + ": " + stats.source.words;
                charCounter.title = charCounter.textContent;
            }
            });
        editor.plugins.get('WordCount').fire('change:words');

        this.translate.languageChanged$.subscribe(()=>{
            editor.plugins.get('WordCount').fire('change:characters');
            editor.plugins.get('WordCount').fire('change:words');
        })
    }

    public initMaxLengthCopyPaste(editor: any, maxLength: number): void {
        editor.plugins.get( 'ClipboardPipeline' ).on( 'inputTransformation', ( evt: any, data: any ) => {
            let currentLengthOfField = editor.plugins.get('WordCount').characters;
            currentLengthOfField -= this.getLengthOfSelectedText(editor);
      
            let contents: any[] = [];
            data.content._children.forEach((child: any) =>  this.getPastedContentThroughRecursiveBreakdown(child, contents));
            let lengthOfPastedContent = 0;
            contents.filter(content => content.data).forEach(content => lengthOfPastedContent += content.data.length);

            if (currentLengthOfField != maxLength && lengthOfPastedContent+currentLengthOfField > maxLength) {
                this.notificationService.showNotification(this.translate.instant("ckeditor.maxlength.copypaste"), "error", undefined, true);
            }
      
            for(let i=contents.length-1; i>=0; i--) {
              if (contents[i].data) {
                if (lengthOfPastedContent+currentLengthOfField > maxLength) {
                  lengthOfPastedContent -= contents[i].data.length;
        
                  if(lengthOfPastedContent+currentLengthOfField < maxLength) {
                    contents[i]._data = contents[i].data.substring(0, maxLength-(lengthOfPastedContent+currentLengthOfField));
                  }
                  else {
                    if(contents[i].parent) {
                      let indexToRemove = contents[i].parent._children.findIndex((child: any) => child === contents[i]);
                      contents[i].parent._removeChildren(indexToRemove);
                    }
                  }
                }
              }
              else {
                if(contents[i]._children.length == 0 && contents[i].parent) {
                  let indexToRemove = contents[i].parent._children.findIndex((child: any) => child === contents[i]);
                  contents[i].parent._removeChildren(indexToRemove);
                }
              }
            }
        });
    }

    public initMaxLengthSelectionReplace(editor: any, maxLength: number): void {
        editor.model.document.selection.on("change", () => {
            let selectedCount = this.getLengthOfSelectedText(editor);
            if (selectedCount > 0) {
                editor.commands.get('input').clearForceDisabled();
                editor.commands.get('link').clearForceDisabled();
            } else if(editor.plugins.get('WordCount').characters >= maxLength) {
                editor.commands.get('input').forceDisabled();
                editor.commands.get('link').forceDisabled();
            }
        })
    }

    protected getLengthOfSelectedText(editor: any): number {
        let selection = editor.model.document.selection;
        let range = selection.getFirstRange();
        let lengthOfSelectedText = 0;
        
        for (let item of range.getItems()) {
            if(item.data)
                lengthOfSelectedText+=item.data.length;
        }
        return lengthOfSelectedText;
    }

    protected getPastedContentThroughRecursiveBreakdown(data: any, contents: any[]): void {
        if(data) {
        contents.push(data);
        }
        
        if (data._children)
        data._children.forEach((child: any) => this.getPastedContentThroughRecursiveBreakdown(child, contents));
    }

    public validateLength(editor: any, maxLength: number): void {
        if (editor.plugins.get('WordCount').characters >= maxLength) {
        editor.commands.get('input').forceDisabled();
        editor.commands.get('link').forceDisabled();
        } else {
        editor.commands.get('input').clearForceDisabled();
        editor.commands.get('link').clearForceDisabled();
        }
    }
}